// The entry point for everything written from here on.
//
// An api module used to be a class holding its own FetchRequest, and a controller next
// to it repeating `try { await ... } catch { console.error }` once per endpoint. Both
// layers exist only to carry the page's toast setter down to the request and to swallow
// the rejection afterwards. NotifyProvider now registers that setter app wide, so a
// single shared client is enough:
//
//   export const usersApi = {
//     list: (data?: UsersFilter) => http.post<UsersListResponse>("/api/users", data),
//   };
//
// `http` rejects on failure, `http.quiet()` resolves to undefined instead — the toast
// and the console entry are raised by the api layer either way, so a page that has
// nothing to add on top of them wants the quiet one.

import FetchRequest from "./FetchRequest";
import { isApiError } from "./apiErrors";

import type { AuthData, RequestData, RequestHeaders } from "types";

export type HttpOptions = {
  headers?: RequestHeaders;
  // Only for the rare call that has to reach a backend other than the signed in one;
  // everything else takes the pair from authStore.
  authdata?: AuthData | null;
};

export type ApiCall<T> = Promise<T>;

export type QuietCall<T> = Promise<T | undefined>;

export type HttpClient = {
  get<T = unknown>(url: string, data?: RequestData | null, options?: HttpOptions): ApiCall<T>;
  post<T = unknown>(url: string, data?: RequestData | null, options?: HttpOptions): ApiCall<T>;
  put<T = unknown>(url: string, data?: RequestData | null, options?: HttpOptions): ApiCall<T>;
  delete<T = unknown>(url: string, data?: RequestData | null, options?: HttpOptions): ApiCall<T>;
};

export type QuietClient = {
  get<T = unknown>(url: string, data?: RequestData | null, options?: HttpOptions): QuietCall<T>;
  post<T = unknown>(url: string, data?: RequestData | null, options?: HttpOptions): QuietCall<T>;
  put<T = unknown>(url: string, data?: RequestData | null, options?: HttpOptions): QuietCall<T>;
  delete<T = unknown>(url: string, data?: RequestData | null, options?: HttpOptions): QuietCall<T>;
};

const transport = new FetchRequest();

const body = (data?: RequestData | null, options?: HttpOptions) => ({
  data: data ?? null,
  headers: options?.headers,
});

const client: HttpClient = {
  get: <T = unknown>(url: string, data?: RequestData | null, options?: HttpOptions) =>
    transport.get<T>(url, options?.authdata, body(data, options)),
  post: <T = unknown>(url: string, data?: RequestData | null, options?: HttpOptions) =>
    transport.post<T>(url, body(data, options), options?.authdata),
  put: <T = unknown>(url: string, data?: RequestData | null, options?: HttpOptions) =>
    transport.put<T>(url, body(data, options), options?.authdata),
  delete: <T = unknown>(url: string, data?: RequestData | null, options?: HttpOptions) =>
    transport.delete<T>(url, body(data, options), options?.authdata),
};

// Anything that is not an ApiError never passed through our own reporting, so it is a
// bug in the calling code rather than a failed request: it still has to be visible.
const swallow = <T>(call: ApiCall<T>): QuietCall<T> =>
  call.catch((error: unknown) => {
    if (!isApiError(error)) console.error("[api] unexpected error:", error);

    return undefined;
  });

const quietClient: QuietClient = {
  get: <T = unknown>(url: string, data?: RequestData | null, options?: HttpOptions) =>
    swallow(client.get<T>(url, data, options)),
  post: <T = unknown>(url: string, data?: RequestData | null, options?: HttpOptions) =>
    swallow(client.post<T>(url, data, options)),
  put: <T = unknown>(url: string, data?: RequestData | null, options?: HttpOptions) =>
    swallow(client.put<T>(url, data, options)),
  delete: <T = unknown>(url: string, data?: RequestData | null, options?: HttpOptions) =>
    swallow(client.delete<T>(url, data, options)),
};

export const http = {
  ...client,
  quiet: (): QuietClient => quietClient,
};
